"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import type {
  CONTENT_TYPES,
  CTA_SOURCES,
  PAGE_SECTIONS,
} from "@punchup/analytics";
import type { VideoLogger } from "./VideoLogger";

export interface PodcastPlaybackContextValue {
  contentType: CONTENT_TYPES;
  podcastId: string;
  podcastSlug: string;
  ctaSource: CTA_SOURCES | null;
  pageSection: PAGE_SECTIONS | null;
  setCtaSource: Dispatch<SetStateAction<CTA_SOURCES | null>>;
}

const PodcastPlaybackContext =
  createContext<PodcastPlaybackContextValue | null>(null);

export function PodcastPlaybackContextProvider({
  contentType,
  podcastId,
  podcastSlug,
  pageSection,
  initialCtaSource = null,
  children,
}: {
  contentType: CONTENT_TYPES;
  podcastId: string;
  podcastSlug: string;
  pageSection: PAGE_SECTIONS | null;
  initialCtaSource?: CTA_SOURCES | null;
  children: ReactNode;
}) {
  const [ctaSource, setCtaSource] = useState<CTA_SOURCES | null>(
    initialCtaSource
  );
  const value = useMemo<PodcastPlaybackContextValue>(
    () => ({
      contentType,
      podcastId,
      podcastSlug,
      pageSection,
      ctaSource,
      setCtaSource,
    }),
    [contentType, podcastId, podcastSlug, pageSection, ctaSource]
  );
  return (
    <PodcastPlaybackContext.Provider value={value}>
      {children}
    </PodcastPlaybackContext.Provider>
  );
}

export function usePodcastPlaybackContext(): PodcastPlaybackContextValue | null {
  return useContext(PodcastPlaybackContext);
}

export function usePodcastPlaybackContextSync(
  logger: VideoLogger | null
): void {
  const ctx = useContext(PodcastPlaybackContext);
  useEffect(() => {
    if (!logger) return;
    if (ctx) {
      logger.setPodcastContext(ctx);
    } else {
      logger.clearPodcastContext();
    }
  }, [logger, ctx]);
}
